import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import clamp from "lodash/clamp";

import {useFormState} from "../../hooks/useFormState";
import Button from "../../ui/button"
import Input from "../../ui/input";

import Spinner from "../../ui/spinner";

import {useRequestExtraAttempts} from "./PurchaseModal.graphql";
import classes from "./PurchaseModal.module.css";
import {analyticsSendEvent} from "../../libs/analytics";

const MIN = 1
const MAX = 9999
const STEP = 10

type props = {
  onClose?: () => void
}


function PurchaseModalHeader() {
  const {t} = useTranslation();

  return <span>{t("components.PurchaseModal.title")}</span>;
}

function PurchaseModalContent({onClose}: props) {
  const {t} = useTranslation();

  const [mutate, mutationResult] = useRequestExtraAttempts();

  const formState = useFormState<{ count: number }>({
    initialValues: {
      count: 50
    },
    preventDefault: true,
    onSubmit: (values) => {
      analyticsSendEvent("buyLimits", {
        count: values.count,
      });

      mutate({
        variables: {
          count: values.count,
        }
      });
    }
  });

  const onPlus = useCallback(() => {
    formState.setValues({count: clamp(+formState.values.count + STEP, MIN, MAX)})
  }, [formState])

  const onMinus = useCallback(() => {
    formState.setValues({count: clamp(+formState.values.count - STEP, MIN, MAX)})
  }, [formState]);

  if (mutationResult.loading) {
    return (
      <div className={classes.root}>
        <Spinner className={classes.spinner}/>
        <div className={classes.spinnerTitle}>{t("common.loading")}</div>
      </div>
    )
  }

  if (mutationResult.data) {
    return (
      <div className={classes.root}>
        <div className={classes.message}>{t("components.PurchaseModal.successMessage")}</div>
        <Button onClick={onClose} size='l' color="success">
          {t("common.ok")}
        </Button>
      </div>
    )
  }


  if (mutationResult.error) {
    return (
      <div className={classes.root}>
        <p className={classes.message}>{t("components.PurchaseModal.errorMessage")}</p>
        <Button onClick={onClose} size='l' color="danger">
          {t("common.ok")}
        </Button>
      </div>
    )
  }


  return (
    <form className={classes.root} onSubmit={formState.submitHandler}>
      <div className={classes.numberSelector}>
        <Button className={classes.changeBtn} color='secondary' type='button' onClick={onMinus}>-</Button>
        <Input
          className={classes.numberInput}
          required
          min={MIN}
          max={MAX}
          type='number'
          name='count'
          value={+formState.values.count}
          onChange={formState.changeHandler}
        />
        <Button className={classes.changeBtn} color='secondary' type='button' onClick={onPlus}>+</Button>
      </div>

      <Button className={classes.submitBtn} type='submit' color='success' size='l'>
        {t("components.PurchaseModal.buy")}
      </Button>
    </form>
  )
}

const PurchaseModal = {
  Header: PurchaseModalHeader,
  Content: PurchaseModalContent
}

export default PurchaseModal;
